import { countdown, rqStatus, shipStatus } from './booking';
import { BOOKING_VESSELS } from './fixtures';
import type { BookingRequest } from './types';

// Mint-Booking-iPad-Detail (trang chi tiết 2:1): dữ liệu dẫn xuất từ một dòng RQ, theo script board (`dr`, `d`, `DP`). Chỉ đọc.

const PORT: Readonly<Record<string, string>> = {
  VNSGN: 'Hồ Chí Minh', VNCLI: 'Cát Lái', VNVUT: 'Vũng Tàu · Cái Mép', VNCMT: 'Cái Mép', VNHPH: 'Hải Phòng', VNDAD: 'Đà Nẵng', SGSIN: 'Singapore', ITGOA: 'Genoa',
  KRKAN: 'Gwangyang', KRPUS: 'Busan', KRINC: 'Incheon', FRFOS: 'Fos-sur-Mer', PHMNN: 'Manila North', SAJED: 'Jeddah', CNSHA: 'Shanghai', CNQZH: 'Qinzhou',
  HKHKG: 'Hong Kong', JPTYO: 'Tokyo', LYMRA: 'Misurata', MUPLU: 'Port Louis', USLAX: 'Los Angeles', USLGB: 'Long Beach', NLRTM: 'Rotterdam',
};
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const port = (code: string): string => PORT[code.trim()] ?? '';

export function addDays(s: string, n: number): string {
  const m = /^(\d{2})\/(\w{3})\/(\d{4})/.exec(s);
  if (!m) return '';
  const d = new Date(Date.UTC(+m[3]!, Math.max(0, MON.indexOf(m[2]!)), +m[1]! + n));
  return `${String(d.getUTCDate()).padStart(2, '0')}/${MON[d.getUTCMonth()]}/${d.getUTCFullYear()}`;
}

export type Tone = 'err' | 'warn' | 'lb' | 'ok' | 'info';
export interface PlanEnd { lab: string; code: string; port: string; date: string; dk: string; was: string; tip: string }
export interface PlanChip { k: string; tone: Tone; dot: string; tip: string }
export interface TodoItem { k: string; who: string; due: string; tone: 'err' | 'warn' | 'lb'; done: boolean; act?: string }
export interface PicRow { who: string; role: string; none: boolean }
export interface InfoRow { k: string; v: string; href?: string; copy?: boolean; bold?: boolean }
export interface PadDetail {
  title: string;
  chips: Array<{ k: string; tip: string; dot?: string; plain?: boolean }>;
  sub: string;
  primary: string;
  second: string;
  vessel: string;
  vesselMeta: string;
  hasChange: boolean;
  changeTip: string;
  a: PlanEnd;
  b: PlanEnd;
  transit: string;
  transitTip: string;
  planChips: PlanChip[];
  ai: boolean;
  carrier: string;
  facts: Array<{ k: string; v: string; warn: boolean }>;
  log: Array<{ k: string; when: string; dot: string }>;
  todo: TodoItem[];
  info: InfoRow[];
  pics: PicRow[];
  links: Array<{ k: string; sub: string; href: string; tip: string }>;
}

const BACKUP: Readonly<Record<string, string>> = { PHUONGHT: 'TRANTPB', NHITHT: 'PHUONGHT', TRANTPB: 'NHITHT' };
// PIC theo khách: mẫu CUSDATA của board (MPIC_CUS), chọn theo băm tên khách như `mpicCus` — chỉ minh hoạ tới khi có hồ sơ khách thật.
const MPIC_CUS = [
  { cs: 'MAITTN', asst: 'AnNPH', acc: 'LIENNTK' },
  { cs: 'ThuyNTT', asst: 'AnNPH', acc: 'LIENNTK' },
  { cs: 'NGOCNTB', asst: '', acc: 'LIENNTK' },
  { cs: 'HOATTN', asst: 'NHINTT', acc: 'DuyenTM' },
] as const;
function cusPic(name: string) {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 9973;
  return MPIC_CUS[h % MPIC_CUS.length]!;
}

function tone(cd: ReturnType<typeof countdown>, warn = false): 'err' | 'warn' | 'lb' {
  if (cd && cd.mins < 0) return 'err';
  if (warn || (cd && cd.mins < 720)) return 'warn';
  return 'lb';
}

export function padDetail(r: BookingRequest): PadDetail {
  const v = BOOKING_VESSELS[r.vessel] ?? BOOKING_VESSELS['none']!;
  const none = r.vessel === 'none';
  const parts = r.route.split(' → ');
  const pol = parts[0] ?? '';
  const pod = parts[parts.length - 1] ?? '';
  const pot = parts.length > 2 ? (parts[1] ?? '') : '';
  const hasBk = !!r.bookingNo;
  const conf = r.tab === 'done' || r.tab === 'notify';
  const done = r.tab === 'done';
  const etdS = /ETD (\d{2}\/\w{3}\/\d{4})/.exec(v.line)?.[1] ?? '';
  const etd = v.change ? v.change.to.slice(0, 11) : etdS;
  const cdSi = countdown(r.siCutoff, done);
  const cdCy = countdown(r.cyCutoff, done);
  const ship = shipStatus(r);
  const status = rqStatus(r);
  const pic = r.owner ?? 'Chưa nhận';
  const cus = cusPic(r.customer);
  const primary = r.tab === 'queue' ? 'Nhận & lấy booking' : r.tab === 'notify' ? 'Báo khách đổi lịch' : hasBk ? 'Gửi Booking Confirmation' : 'Lấy booking';
  const second = r.tab === 'queue' ? 'Nhận RQ' : hasBk ? 'Yêu cầu cấp rỗng' : 'Nhập booking';
  const siK = `SI ${r.siCutoff ?? '—'}${cdSi ? ` · ${cdSi.k}` : ''}`;
  const cyK = `CY ${r.cyCutoff ?? '—'}${cdCy ? ` · ${cdCy.k}` : ''}`;
  const dotOf = { amber: 'var(--d-amber)', blue: 'var(--d-blue)', gray: 'var(--d-gray)' } as const;

  const todo: TodoItem[] = [];
  if (r.tab === 'queue') todo.push({ k: 'Nhận RQ và lấy booking', who: 'Chưa nhận', due: 'hàng chờ chung', tone: 'warn', done: false, act: 'Nhận' });
  if (!hasBk && r.tab !== 'queue') todo.push({ k: 'Nhập số booking từ hãng', who: pic, due: 'theo RQ', tone: 'lb', done: false, act: 'Nhập' });
  if (r.tab === 'notify' && !r.notified) todo.push({ k: 'Báo khách đổi lịch tàu', who: pic, due: v.change ? `ETD ${v.change.to}` : (r.siCutoff ?? '—'), tone: 'warn', done: false, act: 'Báo' });
  if (hasBk && !conf) todo.push({ k: 'Gửi Booking Confirmation cho khách', who: pic, due: 'sau khi có booking', tone: 'lb', done: false, act: 'Gửi' });
  if (r.tab === 'standby') todo.push({ k: 'Chốt hoặc trả chỗ trước hạn', who: pic, due: '25/Oct/2026 10:00', tone: 'warn', done: false, act: 'Chọn' });
  if (hasBk && r.cyWarn) todo.push({ k: 'Hạ cont trước Closing CY', who: pic, due: r.cyCutoff ?? '—', tone: cdCy && cdCy.mins < 0 ? 'err' : 'warn', done: false });
  if (hasBk) todo.push({ k: 'Nộp SI trước cut-off', who: pic, due: r.siCutoff ?? '—', tone: cdSi && cdSi.mins < 0 && !done ? 'err' : 'lb', done });

  const pics: PicRow[] = [];
  const addPic = (role: string, who: string) => {
    const hit = pics.find((p) => !p.none && p.who.toUpperCase() === who.toUpperCase());
    if (hit) hit.role += ` · ${role}`;
    else pics.push({ who: who || 'Chưa giao', role, none: !who });
  };
  addPic('BKG · nhận xử lý', r.owner ?? '');
  addPic('Sale · theo khách', r.sale);
  addPic('CS · theo khách', cus.cs);
  addPic('Sale Asst · theo khách', cus.asst);
  addPic('Kế toán · theo khách', cus.acc);

  return {
    title: r.jobId,
    chips: [{ k: status, tip: 'RQStatus / ShipmentStatus', dot: dotOf[ship.dot] }, ...(hasBk ? [{ k: r.bookingNo!, tip: `Số booking hãng ${r.carrier}`, plain: true }] : [])],
    sub: `${r.customer} · ${r.route} · Sale ${r.sale} · Sales/CS ${r.sale}/${cus.cs}`,
    primary,
    second,
    vessel: v.name,
    vesselMeta: v.line.split(' · ').slice(0, 2).join(' · '),
    hasChange: !!v.change,
    changeTip: v.change ? `ETD ${v.change.from} → ${v.change.to}` : '',
    a: { lab: 'POL · ETD', code: pol, port: port(pol), date: etd || '—', dk: none ? 'chờ chọn chuyến' : 'dự kiến', was: v.change ? `trước là ${v.change.from.slice(0, 11)}` : '', tip: `Theo lịch tàu ${v.name}` },
    b: { lab: 'POD · ETA', code: pod, port: port(pod), date: etd ? addDays(etd, 22) : '—', dk: etd ? 'ước tính' : 'chờ lịch tàu', was: '', tip: 'ETD + transit 22 ngày theo lịch tàu · cập nhật khi hãng báo ETA' },
    transit: none ? '—' : '22 ngày',
    transitTip: pot ? `Chuyển tải tại ${pot} (${port(pot) || pot})` : 'Đi thẳng',
    planChips: [
      { k: hasBk ? (conf ? 'Booking confirmed' : 'Có booking') : 'Chờ booking hãng', tone: hasBk ? (conf ? 'ok' : 'info') : 'warn', dot: hasBk ? (conf ? 'var(--ok-dot)' : 'var(--d-blue)') : 'var(--d-amber)', tip: hasBk ? `Số ${r.bookingNo}` : 'Chưa có số booking' },
      { k: siK, tone: tone(cdSi), dot: 'var(--d-gray)', tip: 'Doc cut-off · nộp SI trước hạn' },
      { k: cyK, tone: tone(cdCy, r.cyWarn), dot: 'var(--d-gray)', tip: r.cyWarn ? 'Cont chưa hạ bãi' : 'Closing CY' },
    ],
    ai: hasBk,
    carrier: r.carrier,
    facts: [
      { k: 'Hãng tàu', v: r.carrier, warn: false },
      { k: 'Số booking', v: r.bookingNo ?? 'Chưa có', warn: !hasBk },
      { k: 'Container', v: r.cont, warn: false },
      { k: 'Tàu / chuyến', v: v.name, warn: false },
      { k: 'Cut-off SI', v: siK.replace(/^SI /, ''), warn: false },
      { k: 'Closing CY', v: cyK.replace(/^CY /, ''), warn: r.cyWarn },
      { k: 'Chủ job (BKG)', v: r.owner ?? 'Tự điền khi nhận', warn: false },
      { k: 'Backup', v: (r.owner && BACKUP[r.owner]) || '—', warn: false },
      { k: 'Cấp rỗng', v: done ? 'Depot Phước Long' : 'Chưa yêu cầu', warn: false },
    ],
    log: [
      { k: `Sale ${r.sale} tạo RQ · hãng ${r.carrier}`, when: '20/Oct 10:14', dot: 'var(--d-gray)' },
      { k: r.owner ? `${r.owner} nhận RQ` : 'Đang ở hàng chờ chung', when: '20/Oct 10:31', dot: 'var(--d-blue)' },
      { k: hasBk ? `Nhập booking ${r.bookingNo} (Hana đọc mail)` : 'Chưa có booking', when: '21/Oct 09:12', dot: 'var(--d-blue)' },
    ],
    todo,
    info: [
      { k: 'Job', v: r.jobId, href: `/ezy/jobs/${r.jobId}`, copy: true },
      { k: 'Số booking', v: r.bookingNo ?? '', copy: true, bold: true },
      { k: 'Hãng tàu', v: r.carrier },
      { k: 'Tàu / chuyến', v: v.name },
      { k: 'Tuyến', v: r.route },
      { k: 'Container', v: r.cont },
    ],
    pics,
    links: [
      { k: `Job ${r.jobId}`, sub: 'Chi tiết job', href: `/ezy/jobs/${r.jobId}`, tip: '' },
      ...(none ? [] : [{ k: `Chuyến ${v.name}`, sub: 'Lịch tàu', href: '/ezy/pricing', tip: '' }]),
      ...(hasBk ? [{ k: 'B/L của booking', sub: 'Chứng từ & B/L', href: '/ezy/docs', tip: '' }] : []),
    ],
  };
}

